import { useEffect, useRef, useState } from 'react';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as ChevronIcon } from 'assets/icons/Chevron-fat.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import CustomSourceModal from 'components/CustomSourceModal/CustomSourceModal';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type ISoursSelector from './interfaces/ISoursSelector';

import styles from './scss/SoursSelector.module.scss';

function SoursSelector({
  options, value, onChange, error, label, placeholder, titleModal, titleField, titleSecondField,
  required,
}: Readonly<ISoursSelector>) {
  const [selected, setSelected] = useState<IOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions = options?.filter((option) => (option.label || option.name || option.value)
    .toLowerCase()
    .includes(searchTerm.toLowerCase()));

  const isOptionSelected = (option: IOption) => (
    selected.some((selectedOption) => selectedOption.value === option.value)
  );

  const handleToggleOption = (option: IOption) => {
    let newSelected: IOption[];

    if (isOptionSelected(option)) {
      newSelected = selected?.filter((item) => item.value !== option.value);
    } else {
      newSelected = [...selected, option];
    }

    setSelected(newSelected);
    onChange(newSelected);
  };

  const handleRemoveSelected = (valueToRemove: string) => {
    const newSelected = selected?.filter((item) => item.value !== valueToRemove);
    setSelected(newSelected);
    onChange(newSelected);
  };

  const getSelectedOptions = () => selected;

  const handleAddCustomSource = () => {
    setOpenModal(!openModal);
  };

  const handleSelectorClick = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: IOption) => {
    handleToggleOption(option);
  };

  const onAddCustomSource = (data: IOption) => {
    setSelected((prev) => [...prev, data]);
    onChange([...selected, data]);
    setIsOpen(false);
    setOpenModal(false);
  };

  return (
    <div className={styles.root} ref={dropdownRef}>
      {label ? <div className={styles.label}>{required ? `${label}*` : label}</div> : null}
      <button
        type="button"
        className={`${styles.selector} ${error ? styles.error : ''}`}
        onClick={handleSelectorClick}
        onKeyDown={handleSelectorClick}
        aria-expanded={isOpen}
        aria-label={label ? `${label} - Open source selector` : 'Open source selector'}
      >
        <div className={styles.selectedItems}>
          {getSelectedOptions().length > 0 ? (
            getSelectedOptions()?.map((option) => (
              <div key={option.value} className={styles.selectedTag}>
                <span>{option.label || option.name || option.value}</span>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSelected(option.value);
                  }}
                  aria-label={`Remove ${option.label || option.name || option.value}`}
                >
                  <CloseIcon />
                </button>
              </div>
            ))
          ) : (
            placeholder && <span className={styles.placeholder}>{placeholder}</span>
          )}
        </div>
        <ChevronIcon className={`${styles.chevron} ${isOpen ? styles.chevronUp : ''}`} />
      </button>
      {error ? <p className={styles.error}>{error}</p> : null}

      {isOpen ? (
        <div className={styles.dropdown}>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <button
            type="button"
            className={styles.addCustomButton}
            onClick={handleAddCustomSource}
          >
            <PlusIcon className={styles.plusIcon} />
            <span>{titleModal}</span>
          </button>

          <div className={styles.optionsList}>
            {filteredOptions?.map((option) => {
              const isSelected = isOptionSelected(option);
              const optionLabel = option.label || option.name || option.value;
              const actionLabel = `${isSelected ? 'Deselect' : 'Select'} ${optionLabel}`;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleOptionClick(option)}
                  onKeyDown={() => handleOptionClick(option)}
                  aria-label={actionLabel}
                >
                  <span>{optionLabel}</span>
                  {isSelected ? (
                    <CheckIcon className={styles.checkIcon} />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      <CustomSourceModal
        openModal={openModal}
        title={titleModal}
        onCancel={() => setOpenModal(false)}
        onAdd={onAddCustomSource}
        titleField={titleField}
        titleSecondField={titleSecondField}
      />
    </div>
  );
}

export default SoursSelector;
